import type { VesselNominationValidatedFormData } from "@features/vessel-nomination/add/schema/vesselNominationFormSchema";
import type {
  VesselNominationDetailsResponse,
  VesselNominationRequestPayload,
} from "@services/vessel-nomination/types";
import { toDate } from "@utils/date-time";

const mapOptionState = (value?: string | boolean): boolean | undefined => {
  if (value === "na") return undefined;
  if (typeof value === "boolean") return value;
  return undefined;
};

const NONE = "NONE";

const mapConditionalField = (hasValue?: string | boolean, text?: string) => {
  console.log("hasValue", hasValue, "text", text);
  if (hasValue === "" || hasValue === undefined) return undefined;
  if (hasValue === false || hasValue === "false" || hasValue === "na") return NONE;
  return text || undefined;
};

const mappedDocuments = (
  requiredDocuments: VesselNominationValidatedFormData["documents"],
  additionalDocuments: VesselNominationValidatedFormData["additionalNominationDocuments"],
) => {
  const documents = Object.entries(requiredDocuments)
    .filter(([, doc]) => doc.url)
    .map(([key, doc]) => ({
      slot: key,
      fileUrl: doc.url!,
      documentType: doc.type!,
      documentName: doc.documentName,
      otherSpecify: doc.otherSpecify,
      issueDate: toDate(doc.issueDate)?.toISOString(),
      expiryDate: toDate(doc.expiryDate)?.toISOString(),
      norTime: toDate(doc.norTime)?.toISOString(),
    }));
  const additionalDocs =
    additionalDocuments
      ?.filter((doc) => doc.documentUrl)
      .map((doc) => ({
        slot: "ADDITIONAL_DOCUMENT",
        fileUrl: doc.documentUrl!,
        documentType: doc.documentType!,
        documentName: doc.documentName!,
        otherSpecify: doc.otherSpecify,
        issueDate: toDate(doc.issueDate)?.toISOString(),
        expiryDate: toDate(doc.expiryDate)?.toISOString(),
      })) || [];

  return [...documents, ...additionalDocs];
};

const mapVesselNominationFormToPayload = (
  formData: VesselNominationValidatedFormData,
  existingNominationDetails?: VesselNominationDetailsResponse,
): VesselNominationRequestPayload => {
  const isEditingNomination = Boolean(existingNominationDetails);

  return {
    ...(isEditingNomination
      ? {
          id: existingNominationDetails!.id,
          status: existingNominationDetails!.status,
          refOrderNumber: existingNominationDetails!.refOrderNumber || undefined,
        }
      : {}),
    vesselId: formData.vesselId,
    callSign: formData.callSign,
    netTonnage: formData.netTonnage,
    grossTonnage: formData.grossTonnage,
    rta: toDate(formData.rta)?.toISOString() || "",
    eta: toDate(formData.eta)?.toISOString() || "",
    summerDwt: formData.summerDwt || 0,
    arrivalDraft: formData.arrivalDraft || 0,
    departureDraft: formData.departureDraft || 0,
    hasHullProtrusion: !!mapOptionState(formData.hasAreaWithProtrusion),
    previousVesselName: formData.previousVesselName || undefined,
    parcels: formData.parcels.map((parcel) => ({
      ...(parcel.id ? { id: parcel.id } : {}),
      customerTerminal: {
        companyCode: parcel.terminal,
      },
      operationType: parcel.operationType,
      cargoType: { cargoCode: parcel.cargoType },
      cargoQuantityCbm: parcel.cargoQuantityCbm,
      cargoQuantityMt: parcel.cargoQuantityMt,
    })),
    technicalDetails: {
      loa: formData.loa,
      ballast: formData.ballast,
      draughtSummer: formData.draughtSummer,
      draughtTropical: formData.draughtTropical,
    },
    arrivalDraught: {
      fwd: formData.arrivalFwd,
      mid: formData.arrivalMid,
      aft: formData.arrivalAft,
      maxTrimExpectedDuringOperations: formData.maxTrim,
    },
    departureDraught: {
      fwd: formData.departureFwd,
      mid: formData.departureMid,
      aft: formData.departureAft,
    },
    displacement: {
      light: formData.light,
      loaded: formData.loaded,
      arrival: formData.arrival,
      departure: formData.departure,
    },
    deadWeight: {
      summerDwt: formData.summerDwt,
      tropicalDwt: formData.tropicalDwt,
    },
    manifolds: {
      size: formData.manifoldsSize,
      numberOfManifolds: formData.manifoldsNumber,
      distanceBetweenCenterOfManifolds: formData.manifoldsDistance,
      manifoldHeightFromGrating: formData.manifoldsHeightGrafting,
      manifoldHeightAboveWaterlineBallast: formData.manifoldsHeightBallast,
      manifoldHeightAboveWaterlineLoaded: formData.manifoldsHeightLoaded,
      manifoldExtremeHeightAboveWaterlineDuringOperations: formData.manifoldsExtremeHeight,
      distanceBowToManifoldToSternBcm: formData.manifoldsDistanceBowBcmStern,
      distanceBowToManifoldToSternScm: formData.manifoldsDistanceBowScmStern,
      distanceInboardFromManifoldToShipsSideRail: formData.manifoldsDistanceInboard,
      terminalLoadingArmConnectionSuitable: mapOptionState(formData.terminalLoadingArmConnection),
      exhaustGasCleaningSystemFitted: mapOptionState(formData.exhaustGasCleaningSystem),
      exhaustGasCleaningSystemType: formData.exhaustGasCleaningSystem
        ? formData.exhaustGasCleaningSystemType
        : undefined,
      inertGasSystemFitted: mapOptionState(formData.isInertGasSystemOperational),
      cotsInInertConditionAndSystemOperational: mapOptionState(
        formData.confirmInertGasSystemFitted,
      ),
    },
    cargoOnboard: {
      vesselCondition: isEditingNomination
        ? {
            conditionCode: formData.vesselCondition || undefined,
          }
        : formData.vesselCondition,
      ...(formData.vesselCondition !== "BL"
        ? {
            cargoType: { cargoCode: formData.cargoType },
            ...(formData.cargoType === "OT"
              ? { cargoTypeOther: { cargoCode: formData.cargoTypeOther } }
              : {}),
            cargoQuantityM3Gov: formData.cargoQuantityM3,
            cargoQuantityMt: formData.cargoQuantity,
          }
        : {}),
    },
    toxicComponentDetails: {
      maximumAcceptablePressureAtShipsManifolds: formData.maxPressureManifolds!,
      ...(formData.vesselCondition !== "BL"
        ? {
            flashPoint: formData.flashPoint,
            sulphurWtPercent: formData.sulphur,
            mercaptan: formData.mercaptan,
            stowedCargoTemperature: formData.stowedCargoTemperature,
            hydrogenSulphidePpm: formData.hydrogenSulphide,
            rvp: formData.rvp,
            colourAstm: formData.colourAstm || undefined,
            benzeneContentVolPercent: formData.benzeneContent,
            highH2sTanksPurged: mapOptionState(formData.areHighH2STanksAdequate),
            avgPressureOfCargoTanksOnArrival: formData.avgPressureCargoTanksArrival,
            crudeOilWashingPlanned: mapOptionState(formData.isCrudeOilWashingOperationPlanned),
          }
        : {}),
    },
    mooringDetails: {
      fwd: formData.mooringFwd,
      mid: formData.mooringMid,
      aft: formData.mooringAft,
      mooringArrangementDeficiencies: mapConditionalField(
        formData.hasMooringDetailsAndDeficiencies,
        formData.mooringDetailsAndDeficiencies,
      ),
      defectsAffectingSafeOperations: mapConditionalField(
        formData.hasAnyDefectsHullMachineryEquipment,
        formData.anyDefectsHullMachineryEquipment,
      ),
      hullProtrusionArea: mapConditionalField(
        formData.hasAreaWithProtrusion,
        formData.areaWithProtrusion,
      ),
    },
    inspectionDetails: {
      lastPscInspectionDate: toDate(formData.lastPscInspection)?.toISOString() || "",
      lastPscInspectionPlace: formData.place,
      lastSireInspectionDate: toDate(formData.lastSireInspection)?.toISOString() || "",
      lastSireInspectionIssuingAuthority: formData.issuingAuthority,
    },
    voyageInformation: {
      lastPortOfCallCode: isEditingNomination
        ? {
            portCode: formData.lastPort,
          }
        : formData.lastPort,
      nextPortOfCallCode: isEditingNomination
        ? {
            portCode: formData.nextPort,
          }
        : formData.nextPort,
      securityLevel: formData.securityLevel || undefined,
    },
    paqDocument: formData.paqDocument.url
      ? {
          fileUrl: formData.paqDocument.url,
          documentName: formData.paqDocument.documentName,
        }
      : {},
    remarks: formData.remarks?.trim() || undefined,
    documents: mappedDocuments(formData.documents, formData.additionalNominationDocuments),
    acknowledged: formData.confirmation || undefined,
  };
};
export default mapVesselNominationFormToPayload;
